import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import type { Message } from '../types/database'

interface AnimatedPathProps {
  pathData: string
  stroke: string
  fill: string
  strokeWidth: string
}

function AnimatedPath({ pathData, stroke, fill, strokeWidth }: AnimatedPathProps) {
  const pathRef = useRef<SVGPathElement>(null)

  useEffect(() => {
    if (pathRef.current) {
      const length = pathRef.current.getTotalLength()
      pathRef.current.style.strokeDasharray = length.toString()
      pathRef.current.style.strokeDashoffset = length.toString()
    }
  }, [pathData])

  return (
    <path
      ref={pathRef}
      className="animated-path"
      d={pathData}
      stroke={stroke}
      fill={fill}
      strokeWidth={strokeWidth}
    />
  )
}

interface RealtimeMessagesCanvasProps {
  drawingId: number
}

export function RealtimeMessagesCanvas({ drawingId }: RealtimeMessagesCanvasProps) {
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    console.log('🔍 RealtimeMessagesCanvas mounted for drawingId:', drawingId)

    async function fetchMessages() {
      try {
        setLoading(true)
        console.log('📥 Fetching messages for solution_id:', drawingId)
        const { data, error } = await supabase
          .from('messages')
          .select('*')
          .eq('drawing_id', drawingId)
          .order('created_at', { ascending: false })

        if (error) {
          console.error('❌ Error fetching messages:', error)
          throw error
        }
        console.log('✅ Fetched messages:', data?.length || 0, 'messages', data)
        setMessages(data || [])
      } catch (err) {
        console.error('❌ Fetch error:', err)
        setError(err instanceof Error ? err.message : 'Failed to fetch messages')
      } finally {
        setLoading(false)
      }
    }

    fetchMessages()

    console.log('🔔 Setting up realtime subscription for solution_id:', drawingId)
    const channel = supabase
      .channel(`messages_${drawingId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'messages',
        filter: `drawing_id=eq.${drawingId}`
      }, (payload) => {
        console.log('🔔 Realtime message received:', payload)

        if (payload.eventType === 'INSERT') {
          console.log('➕ INSERT:', payload.new)
          setMessages((current) => [payload.new as Message, ...current])
        } else if (payload.eventType === 'UPDATE') {
          console.log('🔄 UPDATE:', payload.new)
          setMessages((current) =>
            current.map((msg) =>
              msg.id === payload.new.id ? payload.new as Message : msg
            )
          )
        } else if (payload.eventType === 'DELETE') {
          console.log('🗑️ DELETE:', payload.old)
          setMessages((current) =>
            current.filter((msg) => msg.id !== payload.old.id)
          )
        }
      })
      .subscribe((status) => {
        console.log('📡 Subscription status:', status)
      })

    return () => {
      console.log('🧹 Cleaning up realtime subscription')
      supabase.removeChannel(channel)
    }
  }, [drawingId])

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-center p-8">
          <span className="material-symbols-outlined animate-spin">progress_activity</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
          <span className="material-symbols-outlined text-red-600">error</span>
          <span className="text-sm text-red-800">{error}</span>
        </div>
      </div>
    )
  }

  if (messages.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col items-center justify-center p-8 text-gray-500">
          <span className="material-symbols-outlined text-4xl mb-2">inbox</span>
          <p className="text-sm">실시간 메시지가 없습니다</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 overflow-auto">
      <style>{`
        .animated-path {
          animation: drawStroke 0.5s ease-out forwards;
        }

        @keyframes drawStroke {
          to {
            stroke-dashoffset: 0;
          }
        }
      `}</style>
      <svg
        className="mx-auto"
        viewBox="0 0 600 800"
        style={{ maxWidth: '50%', height: 'auto' }}
      >
        {messages.filter(message => !message.text?.startsWith('DELETE:')).map((message) => {
          const pathMatch = message.text?.match(/<path[^>]*d="([^"]*)"[^>]*\/?>/i)
          const pathData = pathMatch ? pathMatch[1] : message.text
          const strokeMatch = message.text?.match(/stroke="([^"]*)"/i)
          const fillMatch = message.text?.match(/fill="([^"]*)"/i)
          const strokeWidthMatch = message.text?.match(/stroke-width="([^"]*)"/i)

          return (
            <AnimatedPath
              key={message.id}
              pathData={pathData || ''}
              stroke={strokeMatch?.[1] || '#646cff'}
              fill={fillMatch?.[1] || 'none'}
              strokeWidth={strokeWidthMatch?.[1] || '2'}
            />
          )
        })}
      </svg>
      <div className="mt-2 text-center text-sm text-gray-600">
        {messages.length}개의 메시지
      </div>
    </div>
  )
}
